import { DateTime } from 'luxon'

import { CAMPUS_TIME_ZONE } from 'constants/calendars'
import { SemesterShifts, ShiftEvent, TutoringShift } from 'types/tutoringShifts.types'

/** "Cara B. (3300)", the way the Google calendars titled shifts */
export const getShiftSummary = (shift: Pick<TutoringShift, 'name' | 'courses'>): string => {
  return shift.courses.length > 0 ? `${shift.name} (${shift.courses.join(', ')})` : shift.name
}

/**
 * Expand TA Draft's weekly shifts into the events that overlap [fromISO, toISO].
 *
 * A shift repeats over its own dates, falling back to the semester's; one with neither is left
 * off rather than repeated forever.  Shift times are campus-local.
 */
export const getShiftEventsBetween = (
  semesters: SemesterShifts[],
  fromISO: string,
  toISO: string
): ShiftEvent[] => {
  const from = DateTime.fromISO(fromISO)
  const to = DateTime.fromISO(toISO)
  if (!from.isValid || !to.isValid || to < from) return []

  const events: ShiftEvent[] = []

  // Every campus date the range touches (padded a day for viewers in other time zones)
  const firstDay = from.setZone(CAMPUS_TIME_ZONE).startOf('day').minus({ days: 1 })
  const lastDay = to.setZone(CAMPUS_TIME_ZONE).startOf('day').plus({ days: 1 })

  for (const { startDate, endDate, shifts } of semesters) {
    for (const shift of shifts) {
      const shiftFirstDate = shift.startDate ?? startDate
      const shiftLastDate = shift.endDate ?? endDate
      if (!shiftFirstDate || !shiftLastDate) continue

      for (let day = firstDay; day <= lastDay; day = day.plus({ days: 1 })) {
        const isoDate = day.toISODate() || ''
        if (day.weekday !== shift.weekday) continue
        if (isoDate < shiftFirstDate || isoDate > shiftLastDate) continue

        const at = (time: string) =>
          DateTime.fromISO(`${isoDate}T${time}`, { zone: CAMPUS_TIME_ZONE })
        // A TA can move one day's hours in TA Draft; that day runs at those instead
        const moved = shift.changes?.find(change => change.date === isoDate)
        const start = at(moved?.start ?? shift.start)
        const end = at(moved?.end ?? shift.end)
        if (!start.isValid || !end.isValid) continue
        if (end < from || start > to) continue

        // Local ISO strings, because events are compared as text
        events.push({
          summary: getShiftSummary(shift),
          startISO: start.toLocal().toISO() || '',
          endISO: end.toLocal().toISO() || '',
          startTimeISO: start.toLocal().toISOTime() || '',
          endTimeISO: end.toLocal().toISOTime() || '',
          profile: shift.profile,
          ...(moved && {
            usual: {
              startISO: at(shift.start).toLocal().toISO() || '',
              endISO: at(shift.end).toLocal().toISO() || '',
            },
          }),
        })
      }
    }
  }

  return events
}
